import { useState } from "react";
import { MutationError, ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import {
  useDispute,
  useEnterBulkStatement,
  useEnterStatement,
  useReconciliation,
} from "./use-reconciliation";
import {
  LINE_TYPES,
  statusHelpKey,
  statusLabelKey,
  type EntryStatus,
  type ExceptionEntry,
  type LineType,
  type Reconciliation,
} from "./types";

const STATUSES: EntryStatus[] = ["EXPECTED", "RECONCILED", "VARIANCE", "DISPUTED", "CLAWED_BACK"];

// AC6 — the Finance reconciliation dashboard (commission:read@all). Per-carrier
// Expected/Received/gap + the status breakdown + the exceptions surface that
// makes leakage visible. Money rendered as exact decimal strings (never floated);
// a gap reads as calm ink — never alarm red.
export function ReconciliationDashboard() {
  const { t } = useT();
  const [status, setStatus] = useState<EntryStatus | "">("");
  const { data, isLoading, error } = useReconciliation(status ? { status } : {});

  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("recon.heading")}</h1>
          <p className="surface__subtitle">{t("recon.totals")}</p>
        </div>
        <div className="surface__actions">
          <div className="k-select">
            <select
              className="k-select__native"
              aria-label={t("recon.exceptions")}
              value={status}
              onChange={(e) => setStatus(e.target.value as EntryStatus | "")}
            >
              <option value="">{t("recon.totals")}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s} title={t(statusHelpKey(s))}>
                  {t(statusLabelKey(s))}
                </option>
              ))}
            </select>
            <span className="k-select__chevron">
              <Icon name="chevron-down" size={16} />
            </span>
          </div>
        </div>
      </header>

      {isLoading && (
        <div className="k-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("recon.loading")}</span>
          </div>
        </div>
      )}
      {error && (
        <div className="k-note k-note--error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <p role="alert" className="k-note__desc">
              {error instanceof ProblemError ? error.problem.detail : t("recon.error")}
            </p>
          </div>
        </div>
      )}
      {data && <Dashboard recon={data} />}

      <StatementForm />
      <BulkStatementForm />
    </section>
  );
}

function Dashboard({ recon }: { recon: Reconciliation }) {
  const { t } = useT();
  return (
    <>
      <div className="stat-grid" data-testid="recon-totals">
        <div className="k-card">
          <div className="k-stat__top">
            <span className="k-stat__icon k-stat__icon--blue">
              <Icon name="measure" size={18} />
            </span>
          </div>
          <div className="k-stat__value">{recon.totals.expected}</div>
          <div className="k-stat__label">{t("recon.expected")}</div>
        </div>
        <div className="k-card">
          <div className="k-stat__top">
            <span className="k-stat__icon k-stat__icon--green">
              <Icon name="records" size={18} />
            </span>
          </div>
          <div className="k-stat__value">{recon.totals.received}</div>
          <div className="k-stat__label">{t("recon.received")}</div>
        </div>
        {/* The gap is a governance cue, not an alarm: calm amber hairline + ink value. */}
        <div className="k-card k-card--accent-amber">
          <div className="k-stat__top">
            <span className="k-stat__icon k-stat__icon--amber">
              <Icon name="scorecard" size={18} />
            </span>
          </div>
          <div className="k-stat__value" data-testid="total-gap">
            {recon.totals.gap}
          </div>
          <div className="k-stat__label">{t("recon.gap")}</div>
        </div>
      </div>

      <div className="k-tablecard">
        <div className="k-tablecard__head">
          <span className="k-tablecard__title">{t("recon.byCarrier")}</span>
        </div>
        <table className="k-table">
          <thead>
            <tr>
              <th aria-hidden="true" />
              <th className="k-th--num">{t("recon.expected")}</th>
              <th className="k-th--num">{t("recon.received")}</th>
              <th className="k-th--num">{t("recon.gap")}</th>
              <th>{t("recon.exceptions")}</th>
            </tr>
          </thead>
          <tbody>
            {recon.byCarrier.map((c) => (
              <tr key={c.carrierId} data-testid="carrier-row">
                <td>
                  <span className="k-tablecard__title">{c.carrierCode}</span>
                </td>
                <td className="k-td--num">{c.expected}</td>
                <td className="k-td--num">{c.received}</td>
                <td className="k-td--num">{c.gap}</td>
                <td>
                  <span className="recon-counts">
                    {STATUSES.map((s) => (
                      <span key={s} className="k-chip" title={t(statusHelpKey(s))}>
                        {t(statusLabelKey(s))} {c.statusCounts[s]}
                      </span>
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="surface__section-label">{t("recon.exceptions")}</p>
      <ExceptionList
        title={t("recon.variances")}
        testid="ex-variances"
        data={recon.exceptions.variances}
        kind="VARIANCE"
      />
      <ExceptionList
        title={t("recon.disputed")}
        testid="ex-disputed"
        data={recon.exceptions.disputed}
        kind="DISPUTED"
      />
      <ExceptionList
        title={t("recon.clawedBack")}
        testid="ex-clawedBack"
        data={recon.exceptions.clawedBack}
        kind="CLAWED_BACK"
      />

      <div className="k-card k-card--pad-sm" data-testid="ex-unmatched">
        <p
          className="surface__section-label"
          style={{ marginTop: 0 }}
          title={t(statusHelpKey("UNMATCHED"))}
        >
          {t("recon.unmatched")} ({recon.exceptions.unmatchedLines.count})
        </p>
        <ul className="recon-list">
          {recon.exceptions.unmatchedLines.items.map((l) => (
            <li key={l.lineId} className="recon-ex-row">
              <code>{l.carrierReference}</code>
              <span className="k-chip">{l.lineType}</span>
              <span className="recon-ex-amount">{l.amount}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="k-card k-card--pad-sm" data-testid="ex-missingRate">
        <p
          className="surface__section-label"
          style={{ marginTop: 0 }}
          title={t(statusHelpKey("MISSING_RATE"))}
        >
          {t("recon.missingRate")} ({recon.exceptions.missingRate.count})
        </p>
        <ul className="recon-list">
          {recon.exceptions.missingRate.items.map((m) => (
            <li key={m.policyId} className="recon-ex-row">
              <span className="k-badge k-badge--standard">{m.carrierCode}</span>
              <code>{m.carrierPolicyNumber}</code>
              <span className="recon-ex-amount">{m.lineCode}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

// A single exception class (variances / disputed / clawed-back). The status is a
// calm chip; a variance/disputed row carries the dispute controls inline.
function ExceptionList({
  title,
  testid,
  data,
  kind,
}: {
  title: string;
  testid: string;
  data: { count: number; items: ExceptionEntry[] };
  kind: EntryStatus;
}) {
  const { t } = useT();
  return (
    <div className="k-card k-card--pad-sm" data-testid={testid}>
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {title} ({data.count})
      </p>
      {data.items.length === 0 ? (
        <p className="k-empty__desc" style={{ margin: 0 }}>
          {t("recon.none")}
        </p>
      ) : (
        <ul className="recon-list">
          {data.items.map((e) => (
            <li key={e.entryId} className="recon-ex-row" data-testid="exception-entry">
              <span
                className={`k-badge k-badge--${kind === "DISPUTED" ? "warning" : "standard"}`}
                title={t(statusHelpKey(kind))}
              >
                {t(statusLabelKey(kind))}
              </span>
              <code>{e.policyId.slice(0, 8)}</code>
              <span className="recon-ex-amount">
                {t("recon.gap")} {e.gap}
              </span>
              {(kind === "VARIANCE" || kind === "DISPUTED") && (
                <DisputeControls entryId={e.entryId} kind={kind} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DisputeControls({ entryId, kind }: { entryId: string; kind: EntryStatus }) {
  const { t } = useT();
  const dispute = useDispute(entryId);
  // Opening a dispute needs a reason. It was collected via globalThis.prompt?.(),
  // which returns undefined where prompt is unavailable — the button was then
  // inert with nothing on screen. Inline, and the dispute error is surfaced.
  const [opening, setOpening] = useState(false);
  const [reason, setReason] = useState("");
  if (kind === "VARIANCE") {
    return (
      <span className="recon-resolve">
        {!opening ? (
          <button
            type="button"
            className="k-btn k-btn--secondary k-btn--sm"
            disabled={dispute.isPending}
            onClick={() => setOpening(true)}
          >
            {t("recon.dispute.open")}
          </button>
        ) : (
          <>
            <input
              className="k-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("recon.dispute.reasonPrompt")}
              data-testid="dispute-reason"
            />
            <button
              type="button"
              className="k-btn k-btn--secondary k-btn--sm"
              disabled={dispute.isPending || !reason.trim()}
              onClick={() => dispute.mutate({ action: "open", reason: reason.trim() })}
              data-testid="dispute-open-confirm"
            >
              {t("recon.dispute.open")}
            </button>
          </>
        )}
        <MutationError
          error={dispute.error}
          fallback={t("recon.actionFailed")}
          offline={t("common.offline")}
        />
      </span>
    );
  }
  return (
    <span className="recon-resolve">
      <button
        type="button"
        className="k-btn k-btn--soft k-btn--sm"
        disabled={dispute.isPending}
        onClick={() => dispute.mutate({ action: "resolve", resolution: "RECONCILED" })}
      >
        {t("recon.dispute.resolvedReconciled")}
      </button>
      <button
        type="button"
        className="k-btn k-btn--ghost k-btn--sm"
        disabled={dispute.isPending}
        onClick={() => dispute.mutate({ action: "resolve", resolution: "VARIANCE" })}
      >
        {t("recon.dispute.resolvedVariance")}
      </button>
      <MutationError
        error={dispute.error}
        fallback={t("recon.actionFailed")}
        offline={t("common.offline")}
      />
    </span>
  );
}

function StatementForm() {
  const { t } = useT();
  const enter = useEnterStatement();
  const [carrierId, setCarrierId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [total, setTotal] = useState("");
  const [lines, setLines] = useState<
    Array<{ carrierReference: string; lineType: LineType; amount: string }>
  >([{ carrierReference: "", lineType: "PAYMENT", amount: "" }]);

  const setLine = (i: number, patch: Partial<(typeof lines)[number]>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  // GAP-AUDIT-3 D5 — the submit used to `return` silently on an incomplete form
  // while the button stayed enabled: click, no request, no message, nothing on
  // screen changed. The required set is now expressed once and drives BOTH the
  // guard and the button's disabled state, so the control is never live when it
  // cannot act.
  const complete = Boolean(carrierId.trim() && periodStart && periodEnd && total.trim());

  return (
    <form
      className="k-card recon-stmt-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complete) return;
        enter.mutate({
          carrierId: carrierId.trim(),
          periodStart: new Date(periodStart).toISOString(),
          periodEnd: new Date(periodEnd).toISOString(),
          statementTotal: total.trim(),
          lines: lines.filter((l) => l.carrierReference.trim() && l.amount.trim()),
        });
      }}
    >
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("recon.stmt.heading")}
      </p>
      <div className="recon-form-grid">
        <label className="k-field">
          <span className="k-field__label">{t("recon.stmt.carrierId")}</span>
          <input
            className="k-input"
            aria-label={t("recon.stmt.carrierId")}
            value={carrierId}
            onChange={(e) => setCarrierId(e.target.value)}
          />
        </label>
        <label className="k-field">
          <span className="k-field__label">{t("recon.stmt.periodStart")}</span>
          <input
            type="date"
            className="k-input"
            aria-label={t("recon.stmt.periodStart")}
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </label>
        <label className="k-field">
          <span className="k-field__label">{t("recon.stmt.periodEnd")}</span>
          <input
            type="date"
            className="k-input"
            aria-label={t("recon.stmt.periodEnd")}
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </label>
        <label className="k-field">
          <span className="k-field__label">{t("recon.stmt.total")}</span>
          <input
            className="k-input"
            aria-label={t("recon.stmt.total")}
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
        </label>
      </div>
      {lines.map((l, i) => (
        <div key={i} className="recon-stmt-line">
          <input
            className="k-input"
            aria-label={`${t("recon.stmt.ref")} ${i + 1}`}
            placeholder={t("recon.stmt.ref")}
            value={l.carrierReference}
            onChange={(e) => setLine(i, { carrierReference: e.target.value })}
          />
          <div className="k-select">
            <select
              className="k-select__native"
              aria-label={`${t("recon.stmt.lineType")} ${i + 1}`}
              value={l.lineType}
              onChange={(e) => setLine(i, { lineType: e.target.value as LineType })}
            >
              {LINE_TYPES.map((lt) => (
                <option key={lt} value={lt}>
                  {lt}
                </option>
              ))}
            </select>
            <span className="k-select__chevron">
              <Icon name="chevron-down" size={16} />
            </span>
          </div>
          <input
            className="k-input"
            aria-label={`${t("recon.stmt.amount")} ${i + 1}`}
            placeholder={t("recon.stmt.amount")}
            value={l.amount}
            onChange={(e) => setLine(i, { amount: e.target.value })}
          />
        </div>
      ))}
      <div className="recon-form-actions">
        <button
          type="button"
          className="k-btn k-btn--ghost k-btn--sm"
          onClick={() =>
            setLines((ls) => [...ls, { carrierReference: "", lineType: "PAYMENT", amount: "" }])
          }
        >
          {t("recon.stmt.addLine")}
        </button>
      </div>
      {enter.error instanceof ProblemError && (
        <p role="alert" className="k-field__error">
          {enter.error.problem.detail}
        </p>
      )}
      <button
        type="submit"
        className="k-btn k-btn--primary"
        data-testid="recon-stmt-submit"
        disabled={!complete || enter.isPending}
      >
        {t("recon.stmt.submit")}
      </button>
      {!complete && (
        <p className="k-field__hint" data-testid="recon-stmt-incomplete">
          {t("recon.stmt.incomplete")}
        </p>
      )}
    </form>
  );
}

function BulkStatementForm() {
  const { t } = useT();
  const enter = useEnterBulkStatement();
  const [carrierId, setCarrierId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [total, setTotal] = useState("");
  const [refs, setRefs] = useState("");

  const coveredReferences = refs
    .split(/[\s,]+/)
    .map((r) => r.trim())
    .filter(Boolean);

  // D5 (bulk) — same silent-return defect as the single-statement form above.
  const complete = Boolean(
    carrierId.trim() && periodStart && periodEnd && total.trim() && coveredReferences.length > 0,
  );

  return (
    <form
      className="k-card recon-stmt-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complete) return;
        enter.mutate({
          carrierId: carrierId.trim(),
          periodStart: new Date(periodStart).toISOString(),
          periodEnd: new Date(periodEnd).toISOString(),
          statementTotal: total.trim(),
          coveredReferences,
        });
      }}
    >
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("recon.bulk.heading")}
      </p>
      {/* aria-label only (no wrapping-label text) so these don't collide with the
          single-statement form's identically-worded labels. */}
      <div className="recon-form-grid">
        <input
          className="k-input"
          aria-label={`bulk ${t("recon.stmt.carrierId")}`}
          placeholder={t("recon.stmt.carrierId")}
          value={carrierId}
          onChange={(e) => setCarrierId(e.target.value)}
        />
        <input
          type="date"
          className="k-input"
          aria-label={`bulk ${t("recon.stmt.periodStart")}`}
          value={periodStart}
          onChange={(e) => setPeriodStart(e.target.value)}
        />
        <input
          type="date"
          className="k-input"
          aria-label={`bulk ${t("recon.stmt.periodEnd")}`}
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
        />
        <input
          className="k-input"
          aria-label={`bulk ${t("recon.stmt.total")}`}
          placeholder={t("recon.stmt.total")}
          value={total}
          onChange={(e) => setTotal(e.target.value)}
        />
      </div>
      <label className="k-field">
        <span className="k-field__label">{t("recon.bulk.coveredRefs")}</span>
        <textarea
          className="k-input"
          aria-label={t("recon.bulk.coveredRefs")}
          placeholder={t("recon.bulk.coveredRefs")}
          rows={3}
          value={refs}
          onChange={(e) => setRefs(e.target.value)}
        />
      </label>
      {enter.error instanceof ProblemError && (
        <p role="alert" className="k-field__error">
          {enter.error.problem.detail}
        </p>
      )}
      <button
        type="submit"
        className="k-btn k-btn--primary"
        data-testid="recon-bulk-submit"
        disabled={!complete || enter.isPending}
      >
        {t("recon.bulk.submit")}
      </button>
      {!complete && (
        <p className="k-field__hint" data-testid="recon-bulk-incomplete">
          {t("recon.bulk.incomplete")}
        </p>
      )}

      {enter.data && (
        <div className="recon-bulk-result" data-testid="bulk-result">
          <p className="surface__section-label">{t("recon.bulk.result")}</p>
          <p className="k-empty__desc" style={{ margin: "0 0 10px" }}>
            {t("recon.bulk.estimatedNote")}
          </p>
          <ul className="recon-list">
            {enter.data.lines.map((l) => (
              <li key={l.id} className="recon-ex-row">
                <span
                  className={`k-status k-status--${l.matched ? "active" : "pending"}`}
                  title={t(statusHelpKey(l.entryStatus ?? "UNMATCHED"))}
                >
                  <span className="k-status__dot" />
                  {t(statusLabelKey(l.entryStatus ?? "UNMATCHED"))}
                </span>
                {l.variance ? <span className="recon-ex-amount">Δ {l.variance}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
}
